import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';

const API_URL = '/api';

const initialFields = {
  CategoryCode: '', CategoryName: '', Description: '', Status: 'Active'
};

const CategoryFormModal = ({ open, onClose, onSubmit, initialData, isEdit, loading }) => {
  const [fields, setFields] = useState(initialFields);
  const [errors, setErrors] = useState({});
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Fetch current user
  useEffect(() => {
    fetch(`${API_URL}/current-user`)
      .then(res => res.json())
      .then(data => {
        if (data.user) {
          setCurrentUser(data.user);
        }
      })
      .catch(error => {
        console.error('Error fetching current user:', error);
      });
  }, []);

  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        setFields({ ...initialFields, ...initialData });
      } else {
        // Generate next category code when creating new category
        setIsLoadingCode(true);
        generateNextCategoryCode()
          .then(nextCode => {
            setFields(prev => ({ ...prev, CategoryCode: nextCode }));
          })
          .catch(error => {
            console.error('Error generating category code:', error);
            setFields(prev => ({ ...prev, CategoryCode: 'CAT-001' }));
          })
          .finally(() => {
            setIsLoadingCode(false);
          });
      }
      setErrors({});
    }
  }, [open, initialData, isEdit]);

  const generateNextCategoryCode = async () => {
    try {
      const response = await fetch(`${API_URL}/categorylatestcode`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.nextCode;
    } catch (error) {
      console.error('Error fetching next category code:', error);
      return 'CAT-001';
    }
  };

  const handleChange = e => {
    setFields({ ...fields, [e.target.name]: e.target.value });
  };

  const validate = () => {
    const errs = {};
    if (!fields.CategoryName) errs.CategoryName = 'Required';
    if (!fields.Status) errs.Status = 'Required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    const formData = {
      ...fields,
      CreatedBy: currentUser?.UserID || 1,
      UpdatedBy: currentUser?.UserID || 1
    };
    
    try {
      onSubmit(formData);
    } catch (error) {
      console.error('Error saving category:', error);
      alert(error.message || 'An error occurred while saving the category');
    }
  };

  if (!open) return null;

  return (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-2 sm:p-4">
    <div className="bg-[#E5E1DA] rounded-2xl shadow-2xl w-full max-w-lg sm:max-w-2xl lg:max-w-4xl p-4 sm:p-8 relative overflow-y-auto max-h-[90vh]">
      
      {/* Close Button */}
      <button
        className="absolute top-3 right-3 sm:top-4 sm:right-4 text-[#2b6777] hover:text-[#52ab98]"
        onClick={onClose}
      >
        <FaTimes size={18} className="sm:w-5 sm:h-5" />
      </button>

      {/* Title */}
      <h2 className="text-lg sm:text-2xl font-bold text-[#2b6777] mb-4 sm:mb-6 text-center">
        {isEdit ? "Edit Category" : "Create New Category"}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {/* Category Information */}
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md">
          <h3 className="text-base sm:text-lg font-bold text-[#2b6777] mb-3 sm:mb-4">
            Category Information
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {/* Category Code */}
            <div className="flex flex-col gap-2">
              <label className="block text-xs sm:text-sm font-medium text-[#2b6777]">
                Category Code
              </label>
              <input
                name="CategoryCode"
                value={isLoadingCode ? "Loading..." : fields.CategoryCode}
                readOnly
                className="w-full border rounded px-2 sm:px-3 py-2 bg-gray-100 text-gray-700 text-sm"
              />
            </div>

            {/* Category Name */}
            <div className="flex flex-col gap-2">
              <label className="block text-xs sm:text-sm font-medium text-[#2b6777]">
                Category Name *
              </label>
              <input
                name="CategoryName"
                autoComplete="off"
                value={fields.CategoryName}
                onChange={handleChange}
                className={`w-full border rounded px-2 sm:px-3 py-2 text-sm ${
                  errors.CategoryName ? "border-red-400" : "border-[#c8d8e4]"
                }`}
              />
              {errors.CategoryName && (
                <span className="text-xs text-red-500">
                  {errors.CategoryName}
                </span>
              )}
            </div>

            {/* Description */}
            <div className="flex flex-col gap-2 sm:col-span-2">
              <label className="block text-xs sm:text-sm font-medium text-[#2b6777]">
                Description
              </label>
              <textarea
                name="Description"
                autoComplete="off"
                value={fields.Description}
                onChange={handleChange}
                rows="3"
                className="w-full border border-[#c8d8e4] rounded px-2 sm:px-3 py-2 text-sm"
              />
            </div>

            {/* Status */}
            <div className="flex flex-col gap-2">
              <label className="block text-xs sm:text-sm font-medium text-[#2b6777]">
                Status *
              </label>
              <select
                name="Status"
                value={fields.Status}
                onChange={handleChange}
                className={`w-full border rounded px-2 sm:px-3 py-2 text-sm ${
                  errors.Status ? "border-red-400" : "border-[#c8d8e4]"
                }`}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
              {errors.Status && (
                <span className="text-xs text-red-500">{errors.Status}</span>
              )}
            </div>

            {/* Created Date */}
            <div className="flex flex-col gap-2">
              <label className="block text-xs sm:text-sm font-medium text-[#2b6777]">
                Created Date
              </label>
              <input
                value={new Date().toLocaleString()}
                readOnly
                className="w-full border rounded px-2 sm:px-3 py-2 bg-gray-100 text-gray-700 text-sm"
              />
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 mt-4 sm:mt-6">
          <button
            type="button"
            className="px-3 sm:px-4 py-2 rounded bg-gray-200 text-[#2b6777] font-semibold text-sm sm:text-base"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 sm:px-6 py-2 rounded bg-[#52ab98] hover:bg-[#2b6777] text-white font-semibold shadow text-sm sm:text-base"
            disabled={loading}
          >
            {loading
              ? "Saving..."
              : isEdit
              ? "Update Category"
              : "Create Category"}
          </button>
        </div>
      </form>
    </div>
  </div>
);

};

export default CategoryFormModal; 